import React, { memo, useRef, useEffect, useCallback, useMemo, forwardRef, } from "../../../../__compat__/react/main.entry.mjs.mjs";
import { useSpacing } from "../../../../api/spacing/useSpacing.mjs.mjs";
import { useIntersectionObserver } from "../../../../hooks/useIntersectionObserver.mjs.mjs";
import { pickAriaProps, pickComponentCommonProps, pickDataProps, } from "../../../../interfaces/commonProps.mjs.mjs";
import { asyncScrollTo } from "../../../../utils/asyncScrollTo.mjs.mjs";
import { StyledBarrelSelect, StyledOption, StyledBarrelSelectGroup, optionOffset, } from "./BarrelSelect.styles.mjs.mjs";
const BarrelSelectOption = memo(function BarrelSelectOption({ selected = false, id, index, label, value, onMount, onUnMount, onSelect, }) {
    const optionRef = useRef(null);
    const handleClick = useCallback(() => onSelect(value), [onSelect, value]);
    useEffect(() => {
        const optionNode = optionRef.current;
        if (optionNode) {
            onMount(optionNode, index);
            return () => {
                onUnMount(optionNode);
            };
        }
        return () => { };
    }, [index, onMount, onUnMount, value]);
    return (React.createElement(StyledOption, { id: id, ref: optionRef, role: "option", "aria-selected": selected, onClick: handleClick }, label));
});
export function BarrelSelect({ id, options = [], value, onChange: handleSelect = () => { }, ...restProps }) {
    var _a;
    const prevValueRef = useRef(null);
    const listRef = useRef(null);
    const optionElementsListRef = useRef([]);
    const optionIdPrefix = id !== null && id !== void 0 ? id : 'barrel-select';
    const { observer, entry } = useIntersectionObserver({
        root: listRef.current,
        threshold: 0.6,
        rootMargin: `-${optionOffset}px 0px -${optionOffset}px 0px`,
    });
    const generateItemId = useCallback((value) => {
        return `${optionIdPrefix}-option-${value}`;
    }, [optionIdPrefix]);
    const handleOptionMount = useCallback((element, index) => {
        optionElementsListRef.current[index] = element;
        observer === null || observer === void 0 ? void 0 : observer.observe(element);
    }, [observer]);
    const handleOptionUnMount = useCallback((element) => {
        observer === null || observer === void 0 ? void 0 : observer.unobserve(element);
    }, [observer]);
    const entryIndex = useMemo(() => entry
        ? optionElementsListRef.current.indexOf(entry.target)
        : -1, [entry]);
    const valueIndex = useMemo(() => options.findIndex((option) => option.value === value), [options, value]);
    const prevIndex = Math.max(0, valueIndex - 1);
    const nextIndex = Math.min(valueIndex + 1, options.length - 1);
    const activeDescendantId = generateItemId(value);
    const entryValue = (_a = options[entryIndex]) === null || _a === void 0 ? void 0 : _a.value;
    const ariaProps = {
        ...pickAriaProps(restProps),
        'aria-activedescendant': activeDescendantId,
        'aria-valuenow': value,
    };
    // ArrowUp and ArrowDown are a patch for Safari <= 15 where arrows keys
    // don't respect scroll-snap steps
    const handleKeyDown = useCallback((event) => {
        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                handleSelect(options[prevIndex].value);
                break;
            case 'ArrowDown':
                event.preventDefault();
                handleSelect(options[nextIndex].value);
                break;
            default:
                break;
        }
    }, [handleSelect, nextIndex, options, prevIndex]);
    useEffect(() => {
        if (entryValue !== undefined && entryValue !== prevValueRef.current) {
            handleSelect(entryValue);
        }
    }, [entryValue, handleSelect]);
    // Value changed from parent
    useEffect(() => {
        const controlledValueChanged = value !== prevValueRef.current;
        const optionElements = optionElementsListRef.current;
        const targetElement = optionElements[valueIndex];
        if (listRef.current &&
            controlledValueChanged &&
            value !== entryValue &&
            targetElement) {
            const scrollTop = targetElement.offsetTop - optionOffset;
            // Taking advantage of the effect that observer did not report active
            // entry yet for initial scroll action
            const intialScroll = entryValue === undefined;
            if (scrollTop !== listRef.current.scrollTop) {
                // Stop the observation during scrolling
                observer === null || observer === void 0 ? void 0 : observer.disconnect();
                asyncScrollTo(listRef.current, {
                    top: scrollTop,
                    behavior: intialScroll ? 'auto' : 'smooth',
                }, 2000 // Extend timeout to allow for ~100 entries
                )
                    .then(() => {
                    if (observer) {
                        // Restore observation
                        optionElements.forEach((element) => observer.observe(element));
                    }
                })
                    .catch((err) => console.error(err));
            }
        }
        // Store previous values for comparison in next render
        if (controlledValueChanged) {
            prevValueRef.current = value;
        }
    }, [value, valueIndex, entryValue, observer]);
    return (React.createElement(StyledBarrelSelect, { ref: listRef, id: id, tabIndex: 0, role: "listbox", onKeyDown: handleKeyDown, ...ariaProps }, options.map(({ label: optionLabel, value: optionValue }, index) => {
        return (React.createElement(BarrelSelectOption, { key: `${index}-${optionLabel.toLowerCase()}`, index: index, id: generateItemId(optionValue), label: optionLabel, selected: optionValue === value, onMount: handleOptionMount, onUnMount: handleOptionUnMount, onSelect: handleSelect, value: optionValue }));
    })));
}
export const BarrelSelectGroup = forwardRef(function BarrelSelectGroup({ children, ...restProps }, ref) {
    return (React.createElement(StyledBarrelSelectGroup, { ref: ref, role: "group", ...pickComponentCommonProps(restProps), ...pickAriaProps(restProps), ...pickDataProps(restProps), outerSpacing: useSpacing(restProps) }, children));
});
//# sourceMappingURL=BarrelSelect.mjs.mjs.map